/**
 * AssistantModelPin — which model answers this fleet's Workflow Assistant, and
 * who said so (ADR-194).
 *
 * WHY THE STATE IS NOT COLLAPSED. ADR-183 Phase 6's per-turn override is behind
 * a link at the fleet owner's request, and correctly so: it is an occasional
 * experiment, not a per-turn setting. But the same reasoning does not apply to
 * the ANSWER. Measured on spartan 2026-09-21, 25 of 25 recent authoring runs ran
 * on the deployment default while four profiles named gpt-6-astra and the vendor
 * was callable throughout — the control worked and nobody could tell it was not
 * in force. Collapsing the MENU is the ask; collapsing the STATE is how this
 * feature fails twice.
 *
 * EVERY LAYER IS RENDERED, not just the winner: "gpt-6-astra" alone cannot tell
 * an operator whether they chose it, their fleet did, or the deployment did, and
 * those three have different remedies when the answer is wrong.
 */
import { useCallback, useEffect, useState } from 'react'

import { api, type AssistantModelView, type ModelCatalogResponse } from '../api/client'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/**
 * useAssistantModel reads the effective pin and offers the two writes a person
 * owns: their own pin, and clearing it.
 *
 * THE READ IS WRAPPED THE WAY useModelCatalog IS, and for the same recorded
 * reason: `.catch` covers a rejected promise only. An absent or
 * synchronously-throwing `api.getAssistantModel` — an older bundle, a rename, a
 * partially-mocked test double — escapes the effect and unmounts the surface it
 * sits in. 219 tests once failed on exactly that TypeError; an operator mid-edit
 * would have lost the editor instead.
 *
 * A WRITE RE-READS rather than updating optimistically. The server owns the
 * precedence (request > operator > fleet > platform); a client that guessed the
 * winner would eventually disagree with the turn, and the operator would be told
 * one model while another answered.
 */
export function useAssistantModel() {
  const [pin, setPin] = useState<AssistantModelView | null>(null)
  const [pinError, setPinError] = useState<string | null>(null)
  const [pinLoading, setPinLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const [pinProposal, setPinProposal] = useState<{ pr_number: number; url: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setPinLoading(true)
    try {
      const p = api.getAssistantModel?.()
      if (!p) {
        setPinError('the Assistant model could not be read on this client')
        setPinLoading(false)
        return () => { cancelled = true }
      }
      p
        .then(d => { if (!cancelled) { setPin(d); setPinError(null) } })
        .catch(e => { if (!cancelled) setPinError(em(e)) })
        .finally(() => { if (!cancelled) setPinLoading(false) })
    } catch (e) {
      setPinError(em(e))
      setPinLoading(false)
    }
    return () => { cancelled = true }
  }, [nonce])

  const setMine = useCallback(async (choice: { provider: string; model: string }) => {
    setPinError(null)
    try {
      await api.setMyAssistantModel?.(choice)
      setNonce(n => n + 1)
    } catch (e) {
      // THE BACKEND'S OWN WORDS. Its four refusals send an operator to four
      // different places, and a generic "could not save" erases that.
      setPinError(em(e))
    }
  }, [])

  const clearMine = useCallback(async () => {
    setPinError(null)
    try {
      await api.clearMyAssistantModel?.()
      setNonce(n => n + 1)
    } catch (e) {
      setPinError(em(e))
    }
  }, [])

  // ADR-194 D2 — COMPOSE a fleet pin, never write one.
  //
  // The content is rendered SERVER-SIDE (beside the reader) and posted through
  // the one existing propose lane, so a fleet pin still reaches production as a
  // reviewed pull request. Rendering the YAML here instead would let the browser
  // drift from the Go parser without either changing, and the result would be a
  // PR that passes review and produces an inert pin.
  const proposeFleet = useCallback(async (choice: { provider: string; model: string }) => {
    setPinError(null)
    setPinProposal(null)
    try {
      const tpl = await api.getFleetPinTemplate?.(choice.provider, choice.model)
      if (!tpl) {
        setPinError('this Mission Control cannot compose a fleet pin')
        return
      }
      const res = await api.proposeFleetFile?.(tpl.path, tpl.content, tpl.message)
      // WHERE TO APPROVE IT, kept and surfaced: a proposal nobody can find is
      // the same as no proposal.
      if (res) setPinProposal({ pr_number: res.pr_number, url: res.url })
    } catch (e) {
      // The pair is validated before anything is opened, so a refusal arrives
      // here with the platform's own words and NO pull request exists. A
      // reviewer approving a pin CI then rejects is worse than no PR at all.
      setPinError(em(e))
    }
  }, [])

  return { pin, pinError, pinLoading, setMine, clearMine, proposeFleet, pinProposal }
}

/** Human words for the layer that won. */
function sourceLabel(source: string | undefined): string {
  switch (source) {
    case 'request': return 'this turn only'
    case 'operator': return 'pinned by you'
    case 'fleet': return 'pinned by this fleet'
    default: return 'deployment default'
  }
}

/**
 * AssistantModelState is the always-visible line: what will answer, who chose
 * it, and — when the committed file is unreadable — why the fleet's pin is not
 * in force.
 */
export function AssistantModelState({ pin, catalog = null }: {
  pin: AssistantModelView | null
  /** Optional. With it, a pin this Mission Control cannot call is warned about
   *  BEFORE the turn rather than discovered after one. */
  catalog?: ModelCatalogResponse | null
}) {
  if (!pin) return null
  const eff = pin.effective
  const showFleetToo = eff.source !== 'fleet' && !!pin.fleet.model

  // ADR-194 D5, said BEFORE the turn instead of after it. A pin whose vendor
  // this Mission Control holds no key for still SAVES (D6 — callability changes
  // with a redeploy), and the turn then substitutes loudly. Knowing that in
  // advance is strictly better than learning it from a run record, and
  // `mc_callable` is the right fact: it answers "can THIS PROCESS call", which
  // is deliberately not the same question as whether the fleet has a key.
  const prov = catalog?.providers?.find(p => p.provider === eff.provider)
  const uncallable = !!eff.model && !!prov && prov.mc_callable === false

  return (
    <div className="text-xs text-gray-500" data-testid="assistant-model-state">
      <span>
        Assistant model: <span className="font-medium">{eff.model || '—'}</span>
        {' '}<span>({sourceLabel(eff.source)})</span>
      </span>
      {showFleetToo && (
        // The fleet's pin stays visible when something outranks it: an operator
        // overriding their fleet must see what they are overriding, or "why is
        // my fleet's evaluation not running" has no answer where it was caused.
        <span> · this fleet pins <span className="font-medium">{pin.fleet.model}</span></span>
      )}
      {uncallable && (
        <div className="mt-1 text-amber-700">
          This Mission Control cannot call {prov?.label ?? eff.provider} — turns will
          run on {pin.platform.model} until it is redeployed with that vendor's key,
          and each run will record the substitution.
        </div>
      )}
      {pin.fleet_error && (
        // NOT an error state for the control — the effective model is still
        // shown — but the cause is named, with the file, because a committed pin
        // nobody can read must never read as "no pin".
        <div className="mt-1 text-amber-700">
          This fleet declared a pin that could not be read: {pin.fleet_error}
        </div>
      )}
    </div>
  )
}
